import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import ImagePreviewer from '~shared/components/ImageField/ImagePreviewer';
import { RichTextRenderer } from '~shared/components/RichTextRenderer';
import { Stack } from '~shared/components/Stack';
import {
  AnswerData,
  calculateAggregateAnswerData,
} from '~teacher/components/ResultsQuestion/util/calculateAggregateAnswerData';
import { StudentActivityResponse } from '~teacher/models/activities';
import { QuizAnswer } from '~teacher/models/quizzes';

import { answerNumberToLetter } from '../../../util/answerNumberToLetter';
import { calculatePercent } from '../../../util/math';
import { ResultsQuestionAnswerResult } from './ResultsQuestionAnswerResult';

function AnswerLabel({
  children,
  id,
  isCorrect,
  showCorrect,
}): React.ReactElement {
  const styleMap = {
    correct: {
      text: 'text-white',
      bg: 'bg-green-500',
    },
    default: {
      text: 'text-blue-900',
      bg: 'bg-grey-100',
    },
  };
  const styleKey = showCorrect && isCorrect ? 'correct' : 'default';
  const containerClass = cx(
    'relative flex flex-none items-center justify-center w-9 h-9 rounded-round',
    'transition ease-out-snap duration-300',
    styleMap[styleKey].bg
  );
  const iconClass = cx(
    'absolute right-px flex flex-none items-center w-16 h-9 pl-2 rounded-pill',
    'transition-all ease-out-snap duration-300',
    styleMap[styleKey].bg,
    styleMap[styleKey].text,
    showCorrect ? 'max-w-16' : 'max-w-9'
  );
  const labelClass = cx(
    'relative font-display font-700 text-base',
    'transition ease-out-snap duration-300',
    styleMap[styleKey].text
  );
  return (
    <div className="relative w-9 h-9">
      {isCorrect ? (
        <div className={iconClass}>
          <svg
            className="fill-current"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            role="img"
            aria-labelledby={`correct-title-${id}`}
          >
            <title id={`correct-title-${id}`}>{translate('Correct')}</title>
            <path
              fillRule="nonzero"
              d="M13.44 3.94a1.5 1.5 0 012.224 2.007l-.103.114-7 7a1.5 1.5 0 01-2.008.103l-.114-.103-3.5-3.5a1.5 1.5 0 012.008-2.225l.114.103L7.5 9.88l5.94-5.94z"
            />
          </svg>
        </div>
      ) : null}
      <div className={containerClass}>
        <span className={labelClass}>{children}</span>
      </div>
    </div>
  );
}

interface Props {
  answers: QuizAnswer[];
  responses: StudentActivityResponse[];
  showResults: boolean;
  hasCorrectAnswer: boolean;
}

export function ResultsQuestionAnswersMC({
  answers,
  responses,
  showResults,
  hasCorrectAnswer,
}: Props): React.ReactElement {
  const aggregateAnswerData = calculateAggregateAnswerData(answers, responses);

  return (
    <Stack space="4">
      {answers.map((answer) => {
        const imageOutlineClass = cx(
          'absolute inset-0 text-white rounded-1 shadow-inner-outline-1',
          'transition-all ease-out-snap',
          answer.isCorrect ? 'duration-500' : 'duration-300',
          !answer.isCorrect && showResults ? 'delay-300' : '',
          showResults ? 'opacity-100' : 'opacity-0'
        );

        const answerData: AnswerData =
          aggregateAnswerData.answerData[answer.id];
        const score = answerData
          ? calculatePercent({
              numerator: answerData.numberResponses,
              denominator: aggregateAnswerData.totalSingleResponses,
            })
          : 0;

        return (
          <div
            className="grid grid-cols-question-fields max-w-5xl p-0 pl-4 lg:pl-0 flex-1"
            key={answer.id}
            data-testid={`results-question-answer-MC-${answer.id}`}
          >
            <div className="flex items-center h-12">
              <AnswerLabel
                id={answer.id}
                isCorrect={answer.isCorrect}
                showCorrect={showResults}
              >
                {answerNumberToLetter(answer.order)}
              </AnswerLabel>
            </div>
            <div className="">
              <ResultsQuestionAnswerResult
                score={score}
                showResult={showResults}
                isCorrect={answer.isCorrect}
                hasCorrectAnswer={hasCorrectAnswer}
                disableInitialBg
              >
                <Stack space="2">
                  {answer.text ? (
                    <RichTextRenderer
                      key={`answer-${answer.id}`}
                      htmlText={answer.text}
                    />
                  ) : null}
                  {answer.image ? (
                    <div className="relative inline-flex">
                      <ImagePreviewer
                        containerStyle="inline-flex rounded-1"
                        imageStyle="max-h-64 rounded-1"
                        imageURL={answer.image?.urlS}
                        showOutline
                        allowZoom
                        zoomImageURL={answer.image?.url}
                      />
                      <div className={imageOutlineClass} />
                    </div>
                  ) : null}
                  {!answer.text && !answer.image ? (
                    <div className="h-8" />
                  ) : null}
                </Stack>
              </ResultsQuestionAnswerResult>
            </div>
          </div>
        );
      })}
    </Stack>
  );
}
